import { cn } from "@/lib/utils";
import { useTranslation } from "@/shared/i18n";
import type { TranslationKey } from "@/shared/i18n/types";
import * as React from "react";

export interface CopyValueProps {
  value: string;
  /** What is being copied, for the accessible name. */
  labelKey: TranslationKey;
  /** Shown instead of the raw value — e.g. a shortened wallet address. */
  display?: string;
  mono?: boolean;
  className?: string;
}

/**
 * A value nobody should have to retype.
 *
 * Written for Operations: buyer emails and vault ids are moved into the Filedgr
 * dashboard by hand during manual fulfilment, and docs/PROVISIONING.md §8b is
 * explicit that a single wrong character grants the wrong person access. The
 * same argument applies to the identifiers a curious owner copies out of a
 * parcel's ledger record, which is why this lives in `shared/` rather than
 * under `admin/`.
 */
const CopyValue = ({
  value,
  labelKey,
  display,
  mono = false,
  className,
}: CopyValueProps) => {
  const { t } = useTranslation();
  const [copied, setCopied] = React.useState(false);
  const timer = React.useRef<ReturnType<typeof setTimeout>>();

  React.useEffect(
    () => () => {
      if (timer.current) clearTimeout(timer.current);
    },
    []
  );

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      timer.current = setTimeout(() => setCopied(false), 1800);
    } catch {
      // Clipboard access can be refused (insecure origin, permissions).
      // The value is still on screen and selectable — nothing is lost.
    }
  };

  return (
    <button
      type="button"
      onClick={() => void copy()}
      aria-label={`${t(labelKey)}: ${value}`}
      className={cn(
        "group inline-flex max-w-full items-center gap-2 text-left",
        className
      )}
    >
      <span
        className={cn(
          "truncate",
          mono && "font-mono text-[11px] tracking-tight"
        )}
      >
        {display ?? value}
      </span>
      {/*
        Both labels are always rendered, stacked in one grid cell, and only
        their opacity changes.

        Swapping the text instead would resize this element — "Copied" is wider
        than "Copy" — and because the table's columns are sized from their
        content, one click would shove every column sideways. Stacking makes the
        control permanently as wide as the longer of the two words, in any
        language, so nothing can move. It also turns the swap into a cross-fade.
      */}
      <span
        aria-hidden
        className="grid shrink-0 font-heading text-[9px] font-medium uppercase tracking-wide2"
      >
        <span
          className={cn(
            "col-start-1 row-start-1 transition-opacity duration-200",
            copied
              ? "opacity-0"
              : "text-kf-muted opacity-35 group-hover:opacity-100 group-focus-visible:opacity-100"
          )}
        >
          {t("common.copy")}
        </span>
        <span
          className={cn(
            "col-start-1 row-start-1 text-kf-olive transition-opacity duration-200",
            copied ? "opacity-100" : "opacity-0"
          )}
        >
          {t("common.copied")}
        </span>
      </span>
      <span role="status" aria-live="polite" className="sr-only">
        {copied ? t("common.copied") : ""}
      </span>
    </button>
  );
};

export { CopyValue };
